import type { GroupByQuery, PaginatedExtendedGroupByQuery } from '@/api/generated/model';
import { MODEL_ORDER } from '@/domains/pages/model-savings/constants';
import type {
  ModelId,
  ModelMixSegment,
  SavingsScopeData,
  SavingsSegmentSummary,
  SavingsTask,
} from '@/domains/pages/model-savings/types';
import {
  computeLighterCouldUsePercent,
  computeTopTasksSavingsSharePercent,
  mixPercent,
  sumTasksSavingAnnual,
} from '@/domains/pages/model-savings/utils/model-savings.utils';
import { tierGroupNameToModelId } from '@/domains/pages/model-savings/utils/model-savings-tier-names';

export const EMPTY_MIX: ModelMixSegment[] = MODEL_ORDER.map((modelId) => ({ modelId, percent: 0 }));

export const EMPTY_SAVINGS_SCOPE_DATA: SavingsScopeData = {
  savingAnnual: 0,
  spendAnnual: 0,
  savingSharePercent: 0,
  interactionsCount: 0,
  frontierSharePercent: 0,
  frontierNeededPercent: 0,
  lighterCouldUsePercent: 0,
  topOpportunitiesSavingAnnual: 0,
  topOpportunitiesShareOfSavingsPercent: 0,
  recommendedMix: EMPTY_MIX,
  actualMix: EMPTY_MIX,
  optimalMix: EMPTY_MIX,
  actualTierRows: [],
  optimalTierRows: [],
  segments: [],
};

function tierScaledCostsToMix(rows: { tierName: string; scaledCost: number }[]): ModelMixSegment[] {
  const costsByModelId = new Map<ModelId, number>();
  let total = 0;

  for (const row of rows) {
    const modelId = tierGroupNameToModelId(row.tierName);
    if (!modelId) continue;
    costsByModelId.set(modelId, (costsByModelId.get(modelId) ?? 0) + row.scaledCost);
    total += row.scaledCost;
  }

  if (total <= 0) return EMPTY_MIX;

  const leadingModelIds = MODEL_ORDER.slice(0, -1);
  const trailingModelId = MODEL_ORDER[MODEL_ORDER.length - 1]!;
  const leadingPercents = leadingModelIds.map((modelId) =>
    Math.round(((costsByModelId.get(modelId) ?? 0) / total) * 100),
  );
  const trailingPercent = Math.max(
    0,
    100 - leadingPercents.reduce((sum, percent) => sum + percent, 0),
  );

  return [
    ...leadingModelIds.map((modelId, index) => ({
      modelId,
      percent: leadingPercents[index]!,
    })),
    { modelId: trailingModelId, percent: trailingPercent },
  ];
}

/** Scaled cost share by tier for a scope-level actual_tier or optimal_tier query. */
export function tierGroupRowsToMix(rows: GroupByQuery[]): ModelMixSegment[] {
  return tierScaledCostsToMix(
    rows.map((row) => ({
      tierName: row.group_name,
      scaledCost: row.scaled_cost ?? 0,
    })),
  );
}

/** Groups task × tier additional groupby rows by task name. */
export function taskTierGroupRowsByTask(rows: GroupByQuery[]): Map<string, GroupByQuery[]> {
  const rowsByTask = new Map<string, GroupByQuery[]>();

  for (const row of rows) {
    const taskName = row.group_name?.trim();
    if (!taskName) continue;
    const bucket = rowsByTask.get(taskName) ?? [];
    bucket.push(row);
    rowsByTask.set(taskName, bucket);
  }

  return rowsByTask;
}

/** Converts additional groupby tier rows (group_name_2) to tier groupby rows (group_name). */
export function taskTierAdditionalRowsToTierGroupRows(rows: GroupByQuery[]): GroupByQuery[] {
  return rows.flatMap((row) => {
    const tierName = row.group_name_2?.trim();
    if (!tierName) return [];
    return [{ group_name: tierName, scaled_cost: row.scaled_cost }];
  });
}

/** Scaled cost share by tier for each task from a task × tier query. */
export function taskTierGroupRowsToMixByTask(rows: GroupByQuery[]): Map<string, ModelMixSegment[]> {
  const rowsByTask = taskTierGroupRowsByTask(rows);

  const mixesByTask = new Map<string, ModelMixSegment[]>();
  for (const [taskName, taskRows] of rowsByTask) {
    mixesByTask.set(taskName, tierGroupRowsToMix(taskTierAdditionalRowsToTierGroupRows(taskRows)));
  }

  return mixesByTask;
}

function mapTaskRow(
  row: GroupByQuery,
  actualMixByTask: Map<string, ModelMixSegment[]>,
  optimalMixByTask: Map<string, ModelMixSegment[]>,
  actualTierRowsByTask: Map<string, GroupByQuery[]>,
  optimalTierRowsByTask: Map<string, GroupByQuery[]>,
): SavingsTask | null {
  const name = row.group_name?.trim();
  if (!name) return null;

  const savingAnnual = row.potential_savings ?? 0;
  const spendAnnual = row.scaled_cost ?? 0;
  const savingSharePercent = spendAnnual > 0 ? Math.round((savingAnnual / spendAnnual) * 100) : 0;
  const actualMix = actualMixByTask.get(name) ?? EMPTY_MIX;
  const optimalMix = optimalMixByTask.get(name) ?? EMPTY_MIX;
  const actualTierRows = taskTierAdditionalRowsToTierGroupRows(actualTierRowsByTask.get(name) ?? []);
  const optimalTierRows = taskTierAdditionalRowsToTierGroupRows(optimalTierRowsByTask.get(name) ?? []);

  return {
    id: name,
    name,
    savingAnnual,
    spendAnnual,
    savingSharePercent,
    recommendedMix: optimalMix,
    actualMix,
    optimalMix,
    frontierSharePercent: mixPercent(actualMix, 'opus'),
    frontierNeededPercent: mixPercent(optimalMix, 'opus'),
    interactionsCount: row.n_interactions ?? 0,
    mostUsedProvider: row.most_used_provider?.trim() || undefined,
    mostUsedModality: row.most_used_modality?.trim() || undefined,
    actualTierRows,
    optimalTierRows,
    calculationRows: [],
  };
}

export function mapModelSavingsTasks(
  data: PaginatedExtendedGroupByQuery,
  actualMixByTask: Map<string, ModelMixSegment[]> = new Map(),
  optimalMixByTask: Map<string, ModelMixSegment[]> = new Map(),
  actualTierRowsByTask: Map<string, GroupByQuery[]> = new Map(),
  optimalTierRowsByTask: Map<string, GroupByQuery[]> = new Map(),
): SavingsTask[] {
  return data.data
    .map((row) =>
      mapTaskRow(row, actualMixByTask, optimalMixByTask, actualTierRowsByTask, optimalTierRowsByTask),
    )
    .filter((task): task is SavingsTask => task != null);
}

export function mapModelSavingsSegmentSummaries(data: PaginatedExtendedGroupByQuery): SavingsSegmentSummary[] {
  return data.data
    .map((row) => {
      const name = row.group_name?.trim();
      if (!name) return null;
      return {
        name,
        savingAnnual: row.potential_savings ?? 0,
      };
    })
    .filter((segment): segment is SavingsSegmentSummary => segment != null);
}

export function mapModelSavingsScopeData(
  summary: PaginatedExtendedGroupByQuery,
  topOpportunityTasks: SavingsTask[],
  segments: SavingsSegmentSummary[],
  actualTierRows: GroupByQuery[] = [],
  optimalTierRows: GroupByQuery[] = [],
): SavingsScopeData {
  const summaryRow = summary.data[0];

  const savingAnnual =
    summaryRow?.potential_savings ?? topOpportunityTasks.reduce((sum, task) => sum + task.savingAnnual, 0);
  const spendAnnual =
    summaryRow?.scaled_cost ?? topOpportunityTasks.reduce((sum, task) => sum + task.spendAnnual, 0);
  const savingSharePercent = spendAnnual > 0 ? Math.round((savingAnnual / spendAnnual) * 100) : 0;
  const topOpportunitiesSavingAnnual = sumTasksSavingAnnual(topOpportunityTasks);

  const actualMix = tierGroupRowsToMix(actualTierRows);
  const optimalMix = tierGroupRowsToMix(optimalTierRows);

  return {
    savingAnnual,
    spendAnnual,
    savingSharePercent,
    interactionsCount:
      summaryRow?.n_interactions ?? topOpportunityTasks.reduce((sum, task) => sum + task.interactionsCount, 0),
    frontierSharePercent: mixPercent(actualMix, 'opus'),
    frontierNeededPercent: mixPercent(optimalMix, 'opus'),
    lighterCouldUsePercent: computeLighterCouldUsePercent(actualMix, optimalMix),
    topOpportunitiesSavingAnnual,
    topOpportunitiesShareOfSavingsPercent: computeTopTasksSavingsSharePercent(
      topOpportunityTasks,
      savingAnnual,
    ),
    mostUsedProvider: summaryRow?.most_used_provider?.trim() || undefined,
    mostUsedModality: summaryRow?.most_used_modality?.trim() || undefined,
    recommendedMix: optimalMix,
    actualMix,
    optimalMix,
    actualTierRows,
    optimalTierRows,
    segments,
  };
}
